import { Component, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Leaf, RefreshCw, Home } from "lucide-react";
import { captureError } from "@/lib/observability";
import { BRAND } from "@/lib/brand";


interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Optional context string forwarded to observability (e.g. route name). */
  context?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Forward to centralized observability pipeline.
    captureError(error, {
      tags: { source: "react-error-boundary", context: this.props.context ?? "unknown" },
      extra: { componentStack: info.componentStack },
      severity: "error",
    });
    console.error("ErrorBoundary caught:", error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div
          role="alert"
          aria-live="assertive"
          className="flex flex-col items-center justify-center min-h-[60vh] gap-6 px-6 py-12 text-center"
        >
          <div className="relative">
            <div className="absolute inset-0 -m-4 rounded-full bg-primary/5 blur-2xl" aria-hidden />
            <div className="relative w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Leaf className="h-7 w-7 text-primary" aria-hidden />
            </div>
          </div>
          <div className="max-w-md space-y-2">
            <h2 className="font-display text-2xl md:text-3xl font-normal italic tracking-tight">
              Take a breath — we hit a snag.
            </h2>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
              {BRAND.name} ran into an unexpected issue on this screen. Your data is safe.
              Try again, or head back home and pick up where you left off.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button variant="outline" className="rounded-xl min-h-11" onClick={this.handleReset}>
              <RefreshCw className="h-4 w-4 mr-2" aria-hidden /> Try again
            </Button>
            <Button className="rounded-xl min-h-11" onClick={() => { window.location.href = "/"; }}>
              <Home className="h-4 w-4 mr-2" aria-hidden /> Back to home
            </Button>
          </div>
          {import.meta.env.DEV && this.state.error?.message && (
            <pre className="mt-4 max-w-lg text-left text-xs text-muted-foreground/70 bg-muted/40 rounded-lg p-3 overflow-auto">
              {this.state.error.message}
            </pre>
          )}
        </div>
      );

    }
    return this.props.children;
  }
}

export default ErrorBoundary;
